<div *ngIf="isVisible" class="relative z-30" aria-labelledby="modal-title" role="dialog" aria-modal="true">
  <!-- Background backdrop -->
  <div class="fixed inset-0 bg-secondary-900 bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

  <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <!-- Modal panel -->
      <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">

        <!-- Close button -->
        <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
          <button type="button" (click)="onClose()" class="rounded-md bg-white text-gray-400 hover:opacity-90 focus:outline-none">
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="px-4 py-4 lg:px-6 lg:py-6">
          <!-- Error State -->
          <div *ngIf="error$ | async as error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            <p class="font-semibold">Error loading deal:</p>
            <p>{{ error }}</p>
          </div>

          <!-- Deal Details -->
          <div class="space-y-6">

            <!-- Deal Header -->
            <div class="flex items-center space-x-4 pb-6 border-b border-gray-100">
              <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0" [class.animate-pulse]="loading$ | async">
                <span class="text-primary text-xl font-medium" *ngIf="deal$ | async as deal">{{ getInitials(deal.name || '') }}</span>
                <div *ngIf="loading$ | async" class="w-8 h-8 rounded-full bg-gray-100 animate-pulse"></div>
              </div>
              <div class="flex-1">
                <ng-container *ngIf="deal$ | async as deal; else headerSkeleton">
                  <h4 class="text-xl font-bold text-gray-900">{{ deal.name || '—' }}</h4>
                  <p class="text-gray-500 text-sm">{{ deal.formatted_deal_value || deal.deal_value || 'No value specified' }}</p>
                  <div class="flex items-center space-x-2 mt-1">
                    <span [ngClass]="getDealStageClass(deal.deal_stage)" class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                      {{ deal.deal_stage || 'Unknown' }}
                    </span>
                    <span [ngClass]="getDealTypeClass(deal.type)" class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                      {{ deal.type || 'N/A' }}
                    </span>
                  </div>
                </ng-container>
                <ng-template #headerSkeleton>
                  <div class="h-6 bg-gray-100 rounded animate-pulse mb-2"></div>
                  <div class="h-4 bg-gray-100 rounded animate-pulse mb-1 w-3/4"></div>
                  <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                </ng-template>
              </div>
            </div>

            <!-- Deal Information -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Deal Information</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Deal Value</label>
                  <ng-container *ngIf="deal$ | async as deal; else valueSkeleton">
                    <p class="text-gray-900 font-semibold text-lg">{{ deal.formatted_deal_value || deal.deal_value || '—' }}</p>
                  </ng-container>
                  <ng-template #valueSkeleton>
                    <div class="h-6 bg-gray-100 rounded animate-pulse w-1/2"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Close Date</label>
                  <ng-container *ngIf="deal$ | async as deal; else closeDateSkeleton">
                    <p class="text-gray-900">{{ formatCloseDate(deal.close_date || '') }}</p>
                  </ng-container>
                  <ng-template #closeDateSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-2/3"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Deal Stage</label>
                  <ng-container *ngIf="deal$ | async as deal; else stageSkeleton">
                    <span [ngClass]="getDealStageClass(deal.deal_stage)" class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {{ deal.deal_stage || '—' }}
                    </span>
                  </ng-container>
                  <ng-template #stageSkeleton>
                    <div class="h-6 bg-gray-100 rounded animate-pulse w-1/3"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Deal Type</label>
                  <ng-container *ngIf="deal$ | async as deal; else typeSkeleton">
                    <span [ngClass]="getDealTypeClass(deal.type)" class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {{ deal.type || '—' }}
                    </span>
                  </ng-container>
                  <ng-template #typeSkeleton>
                    <div class="h-6 bg-gray-100 rounded animate-pulse w-1/4"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">ICP Score</label>
                  <ng-container *ngIf="deal$ | async as deal; else icpSkeleton">
                    <p class="text-gray-900">{{ deal.icp_score || '—' }}</p>
                  </ng-container>
                  <ng-template #icpSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/4"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                  <ng-container *ngIf="deal$ | async as deal; else leadSourceSkeleton">
                    <p class="text-gray-900">{{ deal.lead_source || '—' }}</p>
                  </ng-container>
                  <ng-template #leadSourceSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/3"></div>
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- Associated Records -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Associated Records</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <ng-container *ngIf="deal$ | async as deal; else companySkeleton">
                    <p class="text-gray-900">{{ deal.company?.name || 'No company assigned' }}</p>
                  </ng-container>
                  <ng-template #companySkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-2/3"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                  <ng-container *ngIf="deal$ | async as deal; else contactSkeleton">
                    <p class="text-gray-900">{{ getContactFullName(deal.contact) }}</p>
                  </ng-container>
                  <ng-template #contactSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Deal Owner</label>
                  <ng-container *ngIf="deal$ | async as deal; else ownerSkeleton">
                    <p class="text-gray-900">{{ deal.owner?.name || 'No owner assigned' }}</p>
                  </ng-container>
                  <ng-template #ownerSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Workspace</label>
                  <ng-container *ngIf="deal$ | async as deal; else workspaceSkeleton">
                    <p class="text-gray-900">{{ deal.workspace?.name || 'No workspace assigned' }}</p>
                  </ng-container>
                  <ng-template #workspaceSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- Timestamps -->
            <div class="border-t border-gray-100 pt-6">
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Record Information</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Created</label>
                  <ng-container *ngIf="deal$ | async as deal; else createdSkeleton">
                    <p class="text-gray-600">{{ formatDate(deal.created_at || '') }}</p>
                  </ng-container>
                  <ng-template #createdSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-3/4"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                  <ng-container *ngIf="deal$ | async as deal; else updatedSkeleton">
                    <p class="text-gray-600">{{ formatDate(deal.updated_at || '') }}</p>
                  </ng-container>
                  <ng-template #updatedSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-3/4"></div>
                  </ng-template>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Action button -->
        <div class="px-4 sm:px-6 pb-6">
          <button
            type="button"
            (click)="onClose()"
            class="w-full rounded-md px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-transparent">
            Close
          </button>
        </div>

      </div>
    </div>
  </div>
</div>